import { CustomMouseEvent, PanoControls } from '../controls';
import { DataProjectionCoordinate, SceneCoordinate } from '../projections';
import { ChangeDetector, getPointScale } from '../utilities';
import {
  Scene,
  SphereGeometry,
  MeshBasicMaterial,
  Vector3,
  AmbientLight
} from 'three';
import { Viewer, ViewerProps } from '../main';
import { clearViewerTooltip, setViewerTooltip } from '../../viewer';
import { RayCaster } from '../ray-caster';
import { t } from '../../../localization';
import { flashColor, imageObsColor, ImageObservation, pointObsColor, PointObservation } from '.';
import { nanoid } from '@reduxjs/toolkit';
import { toast } from '../../../app';

export interface AlignerObservation {
  id: string,
  imageObs: Vector3,
  imageMesh: ImageObservation,
  imageSelected: boolean,
  pointObs: DataProjectionCoordinate,
  pointMesh: PointObservation,
  pointSelected: boolean,
  imageIdentifier: string,
  name: string,
  pose: any[],
  error: number
}

export class CameraAlignerAdv {
  private controls: PanoControls;
  public enabled = false;
  public scene: Scene;
  private observations = [] as AlignerObservation[];
  private imageObsInterval = null;
  private pointObsInterval = null;
  private sphereGeometry = new SphereGeometry(0.05, 20, 20);

  private setObservations: React.Dispatch<any[]>;

  constructor(controls: PanoControls, props: ViewerProps) {
    this.controls = controls;
    this.setObservations = props.setAlignerObservations;

    this.initScene();
  }

  get viewer(): Viewer {
    return this.controls.viewer;
  }

  get cameraList() {
    return this.viewer.cameraList;
  }

  get selecting() {
    return !!this.selected;
  }

  get selected() {
    return this.observations.find(x => x.imageSelected || x.pointSelected);
  }

  get currentRow() {
    return this.observations.indexOf(this.selected);
  }

  get observationType() {
    const selected = this.selected;
    if (!selected) return null;
    return selected.imageSelected ? "image" : "point";
  }

  get moreTooltip() {
    return [
      t(`data-aligner-adv.click-to-add-${this.observationType}`),
      t("data-aligner-adv.right-click-to-cancel")
    ];
  }

  initScene() {
    this.scene = new Scene();
    this.scene.add(new AmbientLight(0xFFFFFF));
  }

  setState(state) {
    this.resetObservations();
    this.enabled = state;
  }

  refreshObservationList() {
    this.setObservations([...this.observations]);
  }

  resetObservations() {
    for (let observation of this.observations) {
      this.scene.remove(observation.imageMesh);
      this.scene.remove(observation.pointMesh);
    }

    this.observations = [];
    this.refreshObservationList();
  }

  reset() {
  }

  addNewObservations(numObservations) {
    for (let i=0; i<numObservations; i++) {
      this.observations.push({
        id: nanoid(),
        imageObs: null,
        imageMesh: null,
        imageHidden: false,
        imageSelected: false,
        pointObs: null,
        pointMesh: null,
        pointHidden: false,
        pointSelected: false,
        imageIdentifier: null,
        name: "data-aligner-adv.no-file",
        pose: [],
        error: null,
        pointIdx: null,
        imageIdx: null
      } as AlignerObservation);
    }

    this.refreshObservationList();
  }

  setErrors(errors) {
    for (let i = 0; i<this.observations.length; i++) {
      this.observations[i].error = errors[i];
    }

    this.refreshObservationList();
  }

  clearErrors() {
    for (let i = 0; i<this.observations.length; i++) {
      this.observations[i].error = null;
    }

    this.refreshObservationList();
  }

  getByID(id) {
    return this.observations.find(x => x.id === id);
  }

  removeByID(id) {
    const observation = this.getByID(id);
    if (!observation) return;

    this.observations = this.observations.filter(x => x.id !== id);
    this.scene.remove(observation.imageMesh);
    this.scene.remove(observation.pointMesh);
    this.cancelAlignment();
  }

  clickImage(event) {
    let currentCamera = this.cameraList.current;

    // Get direction vector
    if (currentCamera != null && !this.viewer.orbitState) {
      let raycaster = new RayCaster(this.viewer, event);
      let direction = raycaster.vectorFromEvent(event);
      let position = currentCamera.position.value;
      let rotation = currentCamera.rotation.array;

      // Allows for observations to overwrite each other
      this.scene.remove(this.observations[this.currentRow].imageMesh);

      toast.success(t('data-aligner-adv.new_point_observation_added'));
      this.observations[this.currentRow].name = currentCamera.name;
      this.observations[this.currentRow].imageIdentifier = currentCamera.id;
      this.observations[this.currentRow].imageObs = direction;
      this.observations[this.currentRow].pose = [
        currentCamera.path,
        position.x, position.y, position.z,
        rotation[0], rotation[1], rotation[2]
      ];

      let imageDir = direction.clone().add(this.viewer.camera.position);

      this.addImageObservationMesh(imageDir);
      clearViewerTooltip();
    }
  }

  clickScene(event) {
    let {coordinate} = this.controls.getPickerResults(event);
    if (!coordinate) {
      console.warn("Cloud position: no result");
      return;
    }

    toast.success(t('data-aligner-adv.new_point_observation_added'));
    let position = new SceneCoordinate(coordinate).toDataProjection();
    this.scene.remove(this.observations[this.currentRow].pointMesh);
    this.observations[this.currentRow].pointObs = position;

    this.addPointObservationMesh(coordinate);
    clearViewerTooltip();
  }

  onMouseUp(event: CustomMouseEvent): boolean {
    if (event.mouseMoved || !this.enabled) {
      return false;
    }

    if (event.isLeftClick) {
      // Get direction vector
      this.mouseClick(event);
      return;
    }

    if (event.isRightClick) {
      if (this.selecting) {
        // Cancel observation selection
        this.cancelAlignment();
        return true;
      }
    }

    return false;
  }

  onMouseMove() {
    const type = this.observationType;

    if ((type === "point") || (type === "image")) {
      setViewerTooltip(this.moreTooltip);
    } else {
      clearViewerTooltip();
    }
  }

  mouseClick(event) {
    let observationToAdd = this.observationType;

    if (observationToAdd === "point") {
      this.clickScene(event);
    } else if (observationToAdd === "image") {
      this.clickImage(event);
    } else {
      return;
    }

    this.refreshObservationList();
  }

  cancelAlignment() {
    clearViewerTooltip();

    this.resetSelectedState();
    this.updateAllMeshes();
    this.refreshObservationList();
  }

  addPointObservationMesh(position) {
    let sphereMaterial = new MeshBasicMaterial({color: pointObsColor});
    let mesh = new PointObservation(this.sphereGeometry, sphereMaterial);

    this.scene.add(mesh);
    this.observations[this.currentRow].pointMesh = mesh;
    this.observations[this.currentRow].pointSelected = false;

    mesh.position.copy(position);
    this.setPointScale(mesh);
  }

  addImageObservationMesh(position) {
    let sphereMaterial = new MeshBasicMaterial({color: imageObsColor});
    let mesh = new ImageObservation(this.sphereGeometry, sphereMaterial);

    this.scene.add(mesh);
    this.observations[this.currentRow].imageMesh = mesh;
    this.observations[this.currentRow].imageSelected = false;

    // update position (new or existing mesh)
    mesh.position.copy(position);
    this.setPointScale(mesh);
  }

  /** @private */
  flashObservation(mesh, defaultColor) {
    let numberOfFlashes = 0;
    const maxNumberOfFlashes = 6;
    const timerDelay = 500;

    const interval = setInterval(() => {
      if (numberOfFlashes % 2 === 0) {
        mesh.material.color.setHex(flashColor);
      } else {
        mesh.material.color.setHex(defaultColor);
      }

      numberOfFlashes++;

      if (numberOfFlashes === maxNumberOfFlashes) {
        mesh.material.color.setHex(defaultColor);
        clearInterval(interval);
      }
    }, timerDelay);

    return interval;
  };

  clearFlashIntervals() {
    if (this.imageObsInterval) {
      clearInterval(this.imageObsInterval);
      this.imageObsInterval = null;
    }

    if (this.pointObsInterval) {
      clearInterval(this.pointObsInterval);
      this.pointObsInterval = null;
    }

    // Reset the color for all observations
    this.observations.forEach(observation => {
      if (observation.imageMesh) {
        let material = observation.imageMesh.material as any;
        material.color.setHex(imageObsColor);
      }

      if (observation.pointMesh) {
        let material = observation.pointMesh.material as any;
        material.color.setHex(pointObsColor);
      }
    });
  }

  flashObservations(id) {
    this.clearFlashIntervals();

    const observation = this.getByID(id);
    if (!observation) return;

    if (observation.imageMesh) {
      this.imageObsInterval = this.flashObservation(
        observation.imageMesh, imageObsColor);
    }

    if (observation.pointMesh) {
      this.pointObsInterval = this.flashObservation(
        observation.pointMesh, pointObsColor);
    }
  };

  setCurrentObservation(id, type) {
    const observation = this.getByID(id);
    if (!observation) return;

    this.resetSelectedState();

    if (type === "image") {
      observation.imageSelected = true;
    } else if (type === "point") {
      observation.pointSelected = true;
    }

    this.updateAllMeshes();
    this.refreshObservationList();
  }

  resetSelectedState() {
    this.observations.forEach(obs => {
      obs.imageSelected = false;
      obs.pointSelected= false;
    });
  }

  redrawImageObsMarkers() {
    if (this.observations === null || this.observations.length === 0) {
      return;
    }

    for (let i=0; i<this.observations.length; i++){
      if (!this.observations[i].imageObs) continue;

      this.scene.remove(this.observations[i].imageMesh);
      const id = this.observations[i].imageIdentifier;
      const current = this.cameraList.getByID(id);
      if (!current) continue;

      let sphereMaterial = new MeshBasicMaterial({color: 0x9900cc});
      let direction = this.observations[i].imageObs;
      let mesh = new ImageObservation(this.sphereGeometry, sphereMaterial);

      const position = current.position.viewer;
      const rotations = current.rotation;

      let meshPos = new Vector3()
        .copy(direction)
        .applyEuler(rotations.inputInverted)
        .applyEuler(rotations.euler)
        .add(position);

      mesh.position.copy(meshPos);

      this.scene.add(mesh);
      this.observations[i].imageMesh = mesh;
      this.observations[i].imageSelected = false;

      this.setPointScale(mesh);
    }
  }

  setPointScale(mesh) {
    const scale = getPointScale(this.viewer.camera, mesh);
    mesh.scale.set(scale, scale, scale);
    mesh.geometry.computeBoundingSphere();
  }

  updateAllMeshes() {
    this.observations.forEach(obs => {
      // Resize point observations and set visibility
      if (obs.pointMesh) {
        this.setPointScale(obs.pointMesh);
        obs.pointMesh.visible = !obs.pointSelected;
      }

      // Resize image ibservations and set visibility
      if (obs.imageMesh && this.cameraList.current){
        const currentID = this.cameraList.current.id;
        const matchingCamera = obs.imageIdentifier === currentID;

        if (matchingCamera  && !this.viewer.orbitState) {
          this.setPointScale(obs.imageMesh);
          obs.imageMesh.visible = !obs.imageSelected;
        } else {
          obs.imageMesh.visible = false;
        }
      }
    });
  }

  update(changeDetector: ChangeDetector) {
    if (!this.enabled || !changeDetector.changed) return;

    this.updateAllMeshes();
  }
}